import { Flex, message } from 'antd';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Category, Document } from 'interfaces/documents';
import { setDocument } from 'slices/documentSlice';
import { moveDocument } from '../../../slices/documentsActions';
import { moveCategory } from '../../../slices/categoriesActions';
import { clearError, clearParams } from '../../../slices/documentsSlice';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { isCategory, isDocument } from '../../../utilities/constants';
import DocumentsToolbar from './DocumentsToolbar';
import ProgressCard, { ProgressCardRef } from 'components/ProgressCard';
import UploadMenu, { UploadMenuRef } from 'components/UploadMenu';
import BaseTableDocuments from './BaseTableDocuments';
import { useModal } from 'hooks/useModal';
import { useModeDocumentsPage } from '../../../hooks/useModeDocumentsPage';
import { useFetchDocumentsPage } from '../../../hooks/useFetchDocumentsPage';
import { ModeDocumentsPageType } from '../../../types';
import { DocumentsPageLayout } from './DocumentsPageLayout';
import { useDocumentsNavigateTo } from '../../../hooks/useDocumentsNavigateTo';
import { MenuItemType } from 'antd/es/menu/interface';

const DocumentsTable = ({
    modeDocument = 'documents',
    hasFileTree = false,
    draggable = false,
    filterActions,
}: {
    modeDocument?: ModeDocumentsPageType;
    hasFileTree?: boolean;
    draggable?: boolean;
    filterActions?: (actions: MenuItemType[]) => MenuItemType[];
}) => {
    const dispatch = useAppDispatch();
    const { openModal: openDocumentMarkupModal } = useModal();

    const { isDeleted } = useModeDocumentsPage(modeDocument);
    const { listDocuments, error } = useAppSelector((state) => state.documents);
    const { fetchData: fetchDataDocuments, categoryId } = useFetchDocumentsPage(modeDocument);
    const { navigateTo } = useDocumentsNavigateTo(modeDocument);

    useEffect(() => {
        if (error) {
            message.error(error.message);
            if (error.code == 'CATEGORY_NOT_FOUND') navigateTo();
            dispatch(clearError());
        }
    }, [error]);

    useEffect(() => {
        dispatch(clearParams());
        fetchData();
    }, [categoryId]);

    const progressCard = useRef<ProgressCardRef>(null);
    const uploadMenuRef = useRef<UploadMenuRef>(null);

    const [selectedItems, setSelectedItems] = useState<(Category | Document)[]>([]);

    const onDrop = useCallback(
        async (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
            const dragData =
                'dataTransfer' in event && event.dataTransfer ? event.dataTransfer.getData('text/plain') : null;

            if (dragData) {
                const items = JSON.parse(dragData);

                for (const item of items) {
                    if (isDocument(item)) {
                        await dispatch(moveDocument({ id: item.id, category_id: Number(categoryId) }));
                    } else if (isCategory(item)) {
                        await dispatch(moveCategory({ id: String(item.id), parent_id: Number(categoryId) }));
                    }
                }
                fetchData();
            } else {
                uploadMenuRef.current?.drop(acceptedFiles as unknown as FileList);
            }
        },
        [categoryId],
    );

    const { getRootProps } = useDropzone({ onDrop });

    const handleDocumentOpen = (document: Document) => {
        dispatch(setDocument(document));
        openDocumentMarkupModal('documentMarkupModal', {}, {
            readonly: isDeleted,
            onSubmit: () => {
                fetchData();
            }
        });
    };

    const fetchData = () => {
        setSelectedItems([]);
        fetchDataDocuments();
    };

    const tableMemo = useMemo(() => {
        return (
            <BaseTableDocuments
                selected={selectedItems}
                modeDocument={modeDocument}
                fetchData={fetchData}
                setSelectedItems={setSelectedItems}
                draggable={draggable}
                filterActions={filterActions}
            />
        );
    }, [listDocuments, selectedItems, fetchData]);

    return (
        <Flex
            vertical
            {...getRootProps({
                onClick: (event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation(),
            })}
        >
            <DocumentsToolbar
                fetchData={fetchData}
                categoryId={categoryId}
                selected={selectedItems}
                modeDocument={modeDocument}
            />

            {hasFileTree ? <DocumentsPageLayout modeDocument={modeDocument} tableMemo={tableMemo} /> : tableMemo}

            <ProgressCard
                ref={progressCard}
                title="Uploading files..."
                onOpenClick={(item: { data: Document }) => item.data && handleDocumentOpen(item.data)}
            ></ProgressCard>
            <UploadMenu
                open={false}
                anchorEl={null}
                uploadref={uploadMenuRef}
                onClose={() => {}}
                onComplete={() => fetchData()}
            ></UploadMenu>
        </Flex>
    );
};

export default DocumentsTable;
